// priceManager.js — 단가마스터 앱(price-manager-49b39, 이카운트 API 연동)의 priceItems 컬렉션을
// 읽기 전용으로 조회해 재고 앱의 raw.cost(A코드 기준 단가 맵)로 변환하는 공용 모듈.
// procurementPlans.js와 동일한 패턴: 2번째 Firebase 앱을 이 프로젝트 전용으로 초기화해서 read-only로 사용.
import { initializeApp, getApp, getApps } from "firebase/app";
import { getAuth, signInAnonymously } from "firebase/auth";
import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";

// 단가마스터 앱의 공개 Firebase 설정. 이 프로젝트는 익명 로그인(request.auth != null)이 있어야 읽기 허용됨
// (2026-07-03부터 Firebase 콘솔에서 익명 로그인 활성화됨).
const PRICE_MANAGER_CONFIG = {
  apiKey: "AIzaSyDfoT7ImaiqGNuvOeBH6gaVUZFI6fEDnZs",
  authDomain: "price-manager-49b39.firebaseapp.com",
  projectId: "price-manager-49b39",
};

function priceManagerApp() {
  return getApps().find((a) => a.name === "priceManager")
    ? getApp("priceManager")
    : initializeApp(PRICE_MANAGER_CONFIG, "priceManager");
}

function priceManagerDb() {
  return getFirestore(priceManagerApp());
}

let signInPromise = null;
function ensureSignedIn() {
  const auth = getAuth(priceManagerApp());
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  if (!signInPromise) {
    signInPromise = signInAnonymously(auth)
      .then((cred) => cred.user)
      .catch((e) => { signInPromise = null; throw e; });
  }
  return signInPromise;
}

// uploads 컬렉션에서 가장 최근 업로드 1건을 찾는다(업로드할 때마다 priceItems가 그 uploadId로 새로 쌓이는 구조).
async function fetchLatestUpload() {
  const snap = await getDocs(collection(priceManagerDb(), "uploads"));
  let latest = null;
  snap.docs.forEach((d) => {
    const data = d.data();
    const t = data.uploadedAt && data.uploadedAt.seconds ? data.uploadedAt.seconds : 0;
    if (!latest || t > latest.t) latest = { id: d.id, t, data };
  });
  return latest;
}

// 단가마스터 앱의 최신 단가 현황을 재고 앱 raw.cost 형태({상품코드: 단가})로 변환해 반환.
// raw.cost는 상품코드(priceItems.productCode) 기준으로 조회한다 — A코드(상품메모)는 ERP 입력이 누락된 경우가 있어
// 매칭 기준에서 제외하고, 두 앱 모두에 100% 채워져 있는 상품코드를 기준으로 삼는다(2026-07-03 변경).
export async function fetchLatestPriceCost() {
  await ensureSignedIn();
  const latestUpload = await fetchLatestUpload();
  const priceItemsCol = collection(priceManagerDb(), "priceItems");
  const q = latestUpload ? query(priceItemsCol, where("uploadId", "==", latestUpload.id)) : priceItemsCol;
  const snap = await getDocs(q);

  const cost = {};
  let count = 0;
  snap.docs.forEach((d) => {
    const x = d.data();
    const code = String(x.productCode || "").trim();
    const price = Number(x.unitPrice);
    if (!code || !Number.isFinite(price)) return;
    cost[code] = price;
    count += 1;
  });

  const updatedAtSec = latestUpload && latestUpload.data.uploadedAt ? latestUpload.data.uploadedAt.seconds : null;
  const updatedAt = updatedAtSec
    ? new Date(updatedAtSec * 1000).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "";

  return { cost, count, updatedAt };
}
